import { PromiseLoaderCache } from "./promise-loader-cache";
import { HexColor, isId, Media, MediaTransformationKind, QUICK_COLORS, Ref, User } from "shared";
import { mediaUrl } from "../../../../../util/media-util";
import { renderSmileyAvatar } from "../../../../../util/smiley-avatar";
import { render, svg } from "lit";
import { modelStore } from "../../../../../state/model/model-store";
import { Icon } from "../../../../../../icons";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import { styleMap } from "lit/directives/style-map.js";

export class ImageLoader {
	public readonly cache = new PromiseLoaderCache<HTMLImageElement | undefined>();
	public readonly urlCache = new Map<string, string>();
	// public readonly userCache = new PromiseLoaderCache<HTMLImageElement>();

	public urlToImage(url: string): HTMLImageElement | undefined {
		return this.cache.getWithFallback(url, async () => {
			return loadImageWithUrl(url);
		});
	}

	private getUrlWithCache(id: string, cb: () => string) {
		let url = this.urlCache.get(id);

		if (url == null) {
			url = cb();
			this.urlCache.set(id, url);
		}

		return url;
	}

	public userToImage(user: Ref<User>, kind: MediaTransformationKind = "thumb") {
		// Try to populate the user immediately	from cache
		user = modelStore.getInstant(user) ?? user;

		if (isId(user)) return;

		const cacheId = user.id + user.color;

		if (user.avatar != null) {
			return this.urlToImage(mediaUrl(user.avatar, kind));
		} else {
			return this.cache.getWithFallback(cacheId, async () => {
				return fetchUserAvatarDefault(user as User);
			});
		}
	}

	public mediaToImage(media: Ref<Media>, kind: MediaTransformationKind = "thumb") {
		return this.urlToImage(mediaUrl(media, kind));
	}

	public iconToImage(icon: Icon, options: { fill?: boolean; color?: HexColor } = {}) {
		return this.urlToImage(
			this.getUrlWithCache(icon.svg + (options.color ?? "") + (options.fill?.toString() ?? ""), () =>
				iconToToUrl(icon, options)
			)
		);
	}

	public svgToImage($svgElement: SVGElement) {
		return this.urlToImage(this.getUrlWithCache($svgElement.outerHTML, () => svgElementToUrl($svgElement)));
	}

	public imageDataToImage(data: string) {
		return this.urlToImage(data);
	}
}

function iconToToUrl(icon: Icon, { fill, color }: { fill?: boolean; color?: HexColor }) {
	const $containerElement = document.createElement("div");

	const svgLitTemplate = svg`
		<svg width="100%" height="100%" viewBox="${icon.viewBox}" xmlns="http://www.w3.org/2000/svg" style="${styleMap({
		color
	})}">
			${fill && icon.svgFilled != null ? unsafeSVG(icon.svgFilled) : unsafeSVG(icon.svg)}
		</svg>
	`;

	render(svgLitTemplate, $containerElement);

	const $svgElement = $containerElement.querySelector("svg")!;

	return svgElementToUrl($svgElement);
}

function svgElementToUrl($svgElement: SVGElement) {
	const outerHTML = $svgElement.outerHTML;
	const blob = new Blob([outerHTML], { type: "image/svg+xml;charset=utf-8" });
	// TODO: We also need to invoke the object URL cleanup function when the image is no longer needed
	return URL.createObjectURL(blob);
}

function loadImageWithUrl(url: string) {
	const $img = new Image();
	return new Promise<HTMLImageElement | undefined>((resolve, reject) => {
		$img.onload = () => {
			resolve($img);
		};
		$img.onerror = (err) => {
			console.error("Could not load image", err);
			resolve(undefined);
		};
		$img.src = url;
	});
}

function fetchUserAvatarDefault(user: User) {
	const $containerElement = document.createElement("div");

	const svgLitTemplate = renderSmileyAvatar({
		name: user.id,
		size: 20,
		square: false,
		primaryColor: user.color,
		colors: QUICK_COLORS
	});

	render(svgLitTemplate, $containerElement);

	const $svgElement = $containerElement.querySelector("svg")!;

	return loadImageWithUrl(svgElementToUrl($svgElement));
}
